'use client';

import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { User, Loader2, AlertCircle, X, ZoomIn } from 'lucide-react';
import { mediaSrc } from '@/contexts/media/utils/mediaSrc';

interface CharacterPortraitDisplayProps {
  primaryPortraitUrl?: string;
  /** Canonical face close-up — shown in the circular avatar (better crop than a
   *  full-body squeezed into a circle). The lightbox still shows the full body. */
  faceUrl?: string;
  characterName?: string;
  isGenerating: boolean;
  generationFailed: boolean;
}

export function CharacterPortraitDisplay({
  primaryPortraitUrl,
  faceUrl,
  characterName,
  isGenerating,
  generationFailed,
}: CharacterPortraitDisplayProps) {
  const t = useTranslations('stories.characters.portraits');
  const [zoomedSrc, setZoomedSrc] = useState<string | null>(null);

  useEffect(() => {
    if (!zoomedSrc) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setZoomedSrc(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zoomedSrc]);

  if (isGenerating) {
    return (
      <div data-testid="portrait-display-generating" className="flex flex-col items-center gap-2 py-4">
        <div className="flex h-[200px] w-[200px] items-center justify-center rounded-full border-2 border-dashed border-primary/50 bg-primary/5 animate-pulse">
          <Loader2 className="h-12 w-12 animate-spin text-primary/50" />
        </div>
        <p className="text-sm text-muted-foreground">{t('generatingPortrait')}</p>
      </div>
    );
  }

  if (generationFailed) {
    return (
      <div data-testid="portrait-display-failed" className="flex flex-col items-center gap-2 py-4">
        <div className="flex h-[200px] w-[200px] items-center justify-center rounded-full border-2 border-dashed border-destructive/50 bg-destructive/5">
          <AlertCircle className="h-12 w-12 text-destructive/50" />
        </div>
        <p className="text-sm text-destructive">{t('generationFailed')}</p>
      </div>
    );
  }

  /**
   * ONE reference: the whole figure.
   *
   * There used to be two tiles — a face close-up beside the full body —
   * because the pages were rendered with both. They are not: a character is
   * one reference now (the close-up was retired with the render that read
   * it), so a second tile is either a picture nothing uses or, for everything
   * made since, an empty frame. Characters drawn before the change still have
   * their old crop in the database; it is simply not what the library shows.
   */
  const tiles: Array<{ src: string; label: string; fit: string }> = [];
  if (primaryPortraitUrl) tiles.push({ src: primaryPortraitUrl, label: t('fullBody'), fit: 'object-contain' });
  else if (faceUrl) tiles.push({ src: faceUrl, label: t('fullBody'), fit: 'object-cover object-center' });

  if (tiles.length > 0) {
    return (
      <div data-testid="portrait-display-image" className="flex flex-col items-center py-4">
        <div className="flex items-start justify-center gap-4">
          {tiles.map((tile) => (
            <div key={tile.label} className="flex flex-col items-center gap-1">
              <button
                type="button"
                onClick={() => setZoomedSrc(tile.src)}
                data-testid="portrait-display-zoom"
                aria-label={t('viewLarger')}
                className="group relative h-[180px] w-[140px] overflow-hidden rounded-lg ring-2 ring-primary/60 bg-muted/30 cursor-zoom-in"
              >
                <Image
                  src={mediaSrc(tile.src)}
                  alt={`${characterName || 'Character'} — ${tile.label}`}
                  fill
                  unoptimized
                  className={tile.fit}
                  sizes="140px"
                />
                <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition group-hover:bg-black/30 group-hover:opacity-100">
                  <ZoomIn className="h-7 w-7 text-white drop-shadow" />
                </span>
              </button>
              <span className="text-xs text-muted-foreground">{tile.label}</span>
            </div>
          ))}
        </div>

        {zoomedSrc && (
          <div
            role="dialog"
            aria-modal="true"
            data-testid="portrait-display-lightbox"
            onClick={() => setZoomedSrc(null)}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
          >
            <button
              type="button"
              onClick={() => setZoomedSrc(null)}
              aria-label={t('close')}
              className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
            >
              <X className="h-6 w-6" />
            </button>
            <div
              className="relative h-full max-h-[85vh] w-full max-w-3xl"
              onClick={(e) => e.stopPropagation()}
            >
              <Image
                src={mediaSrc(zoomedSrc)}
                alt={characterName || 'Character'}
                fill
                unoptimized
                className="object-contain"
                sizes="90vw"
              />
            </div>
            {characterName && (
              <p className="absolute bottom-6 left-0 right-0 text-center text-sm text-white/80">
                {characterName}
              </p>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="portrait-display-empty" className="flex flex-col items-center gap-2 py-4">
      <div className="flex h-[200px] w-[200px] items-center justify-center rounded-full border-2 border-dashed border-muted-foreground/25">
        <User className="h-12 w-12 text-muted-foreground/50" />
      </div>
      <p className="text-sm text-muted-foreground">{t('noPortraitYet')}</p>
    </div>
  );
}
